// ADR-091 Phase 5b.2 — async-job pump.
//
// Parallel to runtime/ssePump.ts. POSTs to /v1/tenants/{tid}/query/jobs,
// then subscribes to the SSE relay at /jobs/{id}/stream. Pumps StreamEvents
// into the SAME Zustand store as the sync pump because the worker emits
// every event via emit_job_event (Phase 5b backend); the StreamEvent
// payload shape matches the sync wire format unchanged.

import { startJob, subscribeJobStream } from "@/api/jobs";
import type { StreamEvent } from "@/types";
import { useChatStore } from "./chatStore";

export interface JobPumpArgs {
  tenantId: string;
  text: string;
  conversationId?: string;
  includeTrace?: boolean;
  topK?: number;
}

export async function pumpJobStream(args: JobPumpArgs, token: string): Promise<void> {
  const store = useChatStore.getState();

  store.appendUserMessage(args.text);
  store.startAssistantMessage();

  const controller = new AbortController();
  store.setAbortController(controller);

  let jobId: string;
  try {
    const created = await startJob(args, token);
    jobId = created.job_id;
    store.setActiveJobId(jobId);
  } catch (err) {
    store.applyEvent({
      type: "error",
      code: "job_create_failed",
      detail: String(err),
    } as StreamEvent);
    store.endStream();
    return;
  }

  await subscribeJobStream(
    args.tenantId,
    jobId,
    token,
    {
      onEvent: (event: StreamEvent) => {
        // Active-controller guard: skip late frames from a pump that a
        // citation-phase resubmit has superseded (its controller is no longer
        // the store's). Without it, a stray late citation would misroute to the
        // new message.
        if (useChatStore.getState().abortController !== controller) return;
        // Defect #3 forensic logging (2026-06-02 click-only e2e audit):
        // operator reported stuck `isStreaming=true` AFTER answer fully
        // streamed. The chain ssePumpJob.onEvent → applyEvent('complete') →
        // case 'complete' is structurally correct so the failure mode
        // must be either (a) the relay never forwards `complete`, or (b)
        // the worker never emits it. Log terminal events here so the next
        // reproduction surfaces in browser dev tools the SECOND the event
        // crosses the wire — distinguishes between "event never arrived"
        // (no log) vs "event arrived but state didn't update" (log present
        // but UI still stuck → indicates batching race or selector bug).
        if (event.type === "complete" || event.type === "error") {
          // eslint-disable-next-line no-console
          console.info(`[stream] terminal event ${event.type} received for job=${jobId}`);
        }
        store.applyEvent(event);
      },
      onError: (err) => {
        store.applyEvent({
          type: "error",
          code: "transport_error",
          detail: String(err),
        } as StreamEvent);
      },
      onClose: () => {
        // Only end the stream if THIS pump is still the active one — a
        // citation-phase resubmit may have superseded it (see onEvent guard).
        if (useChatStore.getState().abortController === controller) store.endStream();
      },
      onActivity: () => store.noteStreamActivity(),
      onReconnect: (attempt, lastEventId) => {
        store.setReconnectState({ attempt, lastEventId });
        // eslint-disable-next-line no-console
        console.info(
          `[ADR-091] reconnecting job=${jobId} attempt=${attempt} last_event_id=${lastEventId}`,
        );
      },
    },
    controller.signal,
  );
}
